/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface IUseClipboard {
	copyToClipboard: (value: string) => void;
	wasCopiedRecently: boolean;
}

export function useClipboard(): IUseClipboard {
	const [ wasCopiedRecently, setWasCopiedRecently ] = useState(false);
	const timeout = useRef(null);

	const copyToClipboard = useCallback<IUseClipboard["copyToClipboard"]>(
		async (value) => {
			try {
				if (timeout.current) {
					clearTimeout(timeout.current);
					timeout.current = null;
				}

				await navigator.clipboard.writeText(value);

				setWasCopiedRecently(true);

				timeout.current = setTimeout(() => {
					setWasCopiedRecently(false);
				}, 2000);
			} catch (err) {
				console.error(err);
			}
		},
		[ setWasCopiedRecently ]
	);

	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	return { wasCopiedRecently, copyToClipboard };
}
